// src/pages/Profile.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, updateDoc, setDoc, deleteDoc, collection, query, where,
  getDocs, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import {
  syncAuthorFieldsOnPosts,
  resolveUserName,
} from '../utils/feedHelpers';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const resolveDisplayName = (userData, authUserData, user) => {
  const candidates = [
    userData?.name,
    userData?.displayName,
    authUserData?.name,
    authUserData?.displayName,
    user?.displayName,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  if (user?.email && typeof user.email === 'string') {
    return user.email.split('@')[0];
  }
  return 'User';
};

const getInitial = (name) => {
  if (!name || typeof name !== 'string') return 'U';
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'U';
};

const formatDate = (dateInput) => {
  if (!dateInput) return 'N/A';
  try {
    if (dateInput.toDate) return dateInput.toDate().toLocaleDateString();
    return new Date(dateInput).toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

const getRoleBadge = (userData) => {
  if (userData?.isAdmin || userData?.role === 'admin')
    return { label: 'Administrator', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' };
  if (userData?.role === 'board')
    return { label: 'Board Director', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
  return { label: 'Member', cls: 'bg-orange/10 text-orange' };
};

const computeProfileCompleteness = (data) => {
  if (!data) return 0;
  const fields = ['name', 'phone', 'company', 'position', 'education', 'skills', 'experience', 'bio'];
  const filled = fields.filter((f) => data[f] && String(data[f]).trim().length > 0).length;
  const hasPhoto = data.photoUrl ? 1 : 0;
  return Math.round(((filled + hasPhoto) / (fields.length + 1)) * 100);
};

/**
 * Extract the Firebase Storage path from a download URL.
 * Download URLs look like:
 *   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>?...
 * Returns null if the URL is not a Firebase Storage download URL.
 */
const getStoragePathFromDownloadUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const match = url.match(/\/o\/([^?]+)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

/* ================================================================== */
/* Shared scroll-lock (reference-counted, matches Navbar/Sidebar)      */
/* ================================================================== */

let bodyScrollLockCount = 0;
const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  if (bodyScrollLockCount === 0) document.body.style.overflow = 'hidden';
  bodyScrollLockCount += 1;
};
const unlockBodyScroll = () => {
  if (typeof document === 'undefined') return;
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount === 0) document.body.style.overflow = '';
};

/* ================================================================== */
/* Small components                                                    */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  const bg = type === 'error' ? 'bg-red-500' : type === 'warning' ? 'bg-yellow-500' : 'bg-green-500';
  const icon =
    type === 'error' ? 'fa-circle-exclamation'
    : type === 'warning' ? 'fa-triangle-exclamation'
    : 'fa-circle-check';
  return (
    <div
      role="status"
      className={`fixed top-20 right-4 z-[80] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 ${bg}`}
    >
      <i className={`fas ${icon}`} aria-hidden="true" />
      {message}
    </div>
  );
};

const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', danger = true, onConfirm, onCancel, busy = false }) => {
  useEffect(() => {
    if (!open || busy) return;
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, busy]);

  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              danger ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange/10'
            }`}
          >
            <i
              className={`fas fa-exclamation-triangle ${danger ? 'text-red-500' : 'text-orange'}`}
              aria-hidden="true"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 ${
              danger ? 'bg-red-500 hover:bg-red-600' : 'bg-orange hover:bg-orange-dark'
            }`}
          >
            {busy && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteAccountModal = ({ open, busy, onClose, onConfirm }) => {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTyped('');
      setError('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open || busy) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  useEffect(() => {
    if (!open) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open]);

  if (!open) return null;

  const matches = typed.trim().toUpperCase() === 'DELETE';

  const handleConfirm = () => {
    if (!matches) {
      setError('Type DELETE exactly to confirm.');
      return;
    }
    onConfirm();
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <i className="fas fa-user-slash text-red-500 text-xl" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3
              id="delete-account-title"
              className="text-lg font-bold text-gray-800 dark:text-white mb-1"
            >
              Delete your account?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This is <strong>permanent</strong> and cannot be undone.
            </p>
          </div>
        </div>

        <ul className="mb-4 space-y-1.5 text-xs text-gray-600 dark:text-gray-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <li className="flex items-start gap-2">
            <i className="fas fa-times text-red-500 text-[10px] mt-1" aria-hidden="true" />
            Your profile, posts, comments, and messages
          </li>
          <li className="flex items-start gap-2">
            <i className="fas fa-times text-red-500 text-[10px] mt-1" aria-hidden="true" />
            Your profile photo and uploaded media
          </li>
          <li className="flex items-start gap-2">
            <i className="fas fa-times text-red-500 text-[10px] mt-1" aria-hidden="true" />
            Access to chat, feed, and members directory
          </li>
        </ul>

        <label
          htmlFor="delete-confirm-input"
          className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
        >
          Type <span className="font-mono font-bold text-red-600 dark:text-red-400">DELETE</span> to confirm
        </label>
        <input
          ref={inputRef}
          id="delete-confirm-input"
          type="text"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleConfirm();
          }}
          placeholder="DELETE"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono uppercase tracking-wider text-center disabled:opacity-50"
        />

        {error && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            {error}
          </p>
        )}

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !matches}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                Deleting…
              </>
            ) : (
              <>
                <i className="fas fa-trash-alt" aria-hidden="true" />
                Delete Account
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-full bg-orange/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <i className={`fas ${icon} text-orange text-sm`} aria-hidden="true" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 dark:text-white break-words">
        {value || '—'}
      </p>
    </div>
  </div>
);

const TabButton = ({ active, onClick, icon, label, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-orange text-orange'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-orange'
    }`}
  >
    <i className={`fas ${icon}`} aria-hidden="true" />
    <span className="hidden sm:inline">{label}</span>
    {count !== undefined && count > 0 && (
      <span className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 rounded-full">
        {count}
      </span>
    )}
  </button>
);

/* ================================================================== */
/* Profile Page                                                        */
/* ================================================================== */

const Profile = () => {
  const { user, isApproved, approvalStatus, userData: authUserData } = useAuth();
  const { uid: paramUid } = useParams();
  const navigate = useNavigate();

  const targetUid = paramUid || user?.uid;
  const isOwnProfile = !paramUid || paramUid === user?.uid;

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [myEvents, setMyEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    position: '',
    education: '',
    certifications: '',
    skills: '',
    experience: '',
    bio: '',
  });

  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);
  const [dragActive, setDragActive] = useState(false);

  const functions = useMemo(() => getFunctions(), []);
  const deleteUserAccount = useMemo(
    () => httpsCallable(functions, 'deleteUserAccount'),
    [functions],
  );

  const showToast = (text, type = 'success') => setToast({ text, type });

  const isUserApproved =
    isApproved === true ||
    authUserData?.approved === true ||
    authUserData?.isApproved === true ||
    approvalStatus === 'approved';

  const isUserPending =
    isOwnProfile &&
    (approvalStatus === 'pending_approval' ||
      (!isUserApproved && authUserData?.approved === false));

  const displayName = useMemo(
    () => resolveDisplayName(userData, authUserData, user),
    [userData, authUserData, user],
  );

  const initial = useMemo(() => getInitial(displayName), [displayName]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = isOwnProfile
      ? 'Your Profile · IIEE-ERCSA'
      : `${displayName} · IIEE-ERCSA`;
    return () => {
      document.title = prev;
    };
  }, [isOwnProfile, displayName]);

  /* ---------------- Body scroll lock for edit modal ---------------- */
  useEffect(() => {
    if (!modalOpen) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [modalOpen]);

  /* ---------------- Fetch profile ---------------- */
  useEffect(() => {
    if (!targetUid) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const userDocRef = doc(db, 'users', targetUid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          if (isOwnProfile && user) {
            const defaultData = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              name: user.displayName || user.email.split('@')[0],
              role: 'member',
              isApproved: false,
              approved: false,
              registrationStatus: 'pending_approval',
              isBlocked: false,
              isAdmin: false,
              phone: '',
              company: '',
              position: '',
              education: '',
              certifications: '',
              skills: '',
              experience: '',
              bio: '',
              photoUrl: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            await setDoc(userDocRef, defaultData);
            if (!cancelled) {
              setUserData(defaultData);
              setFormData({
                name: defaultData.name,
                phone: '',
                company: '',
                position: '',
                education: '',
                certifications: '',
                skills: '',
                experience: '',
                bio: '',
              });
            }
          } else if (!cancelled) {
            setNotFound(true);
          }
        } else {
          const data = userDoc.data();
          if (!cancelled) {
            setUserData(data);
            setFormData({
              name: data.name || data.displayName || '',
              phone: data.phone || '',
              company: data.company || '',
              position: data.position || '',
              education: data.education || '',
              certifications: data.certifications || '',
              skills: data.skills || '',
              experience: data.experience || '',
              bio: data.bio || '',
            });
          }
        }

        if (isOwnProfile && isUserApproved && !cancelled) {
          try {
            const eventsQuery = query(
              collection(db, 'events'),
              where('attendees', 'array-contains', targetUid),
              orderBy('date', 'asc'),
            );
            const eventsSnap = await getDocs(eventsQuery);
            const eventsData = eventsSnap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              date: d.data().date?.toDate ? d.data().date.toDate() : null,
            }));
            if (!cancelled) setMyEvents(eventsData);
          } catch (e) {
            console.warn('Events fetch failed', e);
          }
        }
      } catch (err) {
        console.error('Profile fetch error:', err);
        if (!cancelled) {
          showToast(`Failed to load profile: ${err.message}`, 'error');
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [targetUid, isOwnProfile, isUserApproved, user]);

  /* ---------------- Photo upload ---------------- */
  const uploadPhotoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast('Image must be less than 2MB', 'error');
      return;
    }

    setUploadingPhoto(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `profile_${targetUid}_${Date.now()}.${fileExt}`;
      const storageRef = ref(storage, `profiles/${fileName}`);
      await uploadBytes(storageRef, file);
      const photoUrl = await getDownloadURL(storageRef);
      const photoPath = storageRef.fullPath;

      // Best-effort cleanup of the old photo — use the derived storage path,
      // NOT the download URL (which is not a valid Storage reference).
      const oldPath = getStoragePathFromDownloadUrl(userData?.photoUrl);
      if (oldPath) {
        try {
          await deleteObject(ref(storage, oldPath));
        } catch (err) {
          console.warn('Old photo delete skipped', err);
        }
      }

      await updateDoc(doc(db, 'users', targetUid), {
        photoUrl,
        photoPath,
        updatedAt: serverTimestamp(),
      });
      setUserData((prev) => ({ ...prev, photoUrl, photoPath }));
      showToast('Profile photo updated!');

      const authorName =
        resolveUserName(userData) || userData?.name || displayName || 'User';
      syncAuthorFieldsOnPosts(targetUid, {
        authorName,
        authorPhoto: photoUrl,
      })
        .then((n) => {
          if (n > 0) console.info(`Synced photo to ${n} post(s).`);
        })
        .catch((err) => console.warn('Post author sync failed:', err));
    } catch (err) {
      console.error(err);
      showToast('Upload failed', 'error');
    } finally {
      if (mountedRef.current) setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoUpload = (e) => uploadPhotoFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) uploadPhotoFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const removePhoto = () => {
    setConfirmState({
      title: 'Remove profile photo?',
      message: 'Your profile will show your initials instead.',
      confirmLabel: 'Remove',
      onConfirm: async () => {
        try {
          // Derive the real storage path — deleting by download URL silently fails.
          const oldPath =
            userData?.photoPath ||
            getStoragePathFromDownloadUrl(userData?.photoUrl);
          if (oldPath) {
            try {
              await deleteObject(ref(storage, oldPath));
            } catch (err) {
              console.warn('Photo delete skipped', err);
            }
          }
          await updateDoc(doc(db, 'users', targetUid), {
            photoUrl: null,
            photoPath: null,
            updatedAt: serverTimestamp(),
          });
          setUserData((prev) => ({ ...prev, photoUrl: null, photoPath: null }));
          showToast('Photo removed');

          const authorName =
            resolveUserName(userData) || userData?.name || displayName || 'User';
          syncAuthorFieldsOnPosts(targetUid, {
            authorName,
            authorPhoto: null,
          }).catch((err) => console.warn('Post author sync failed:', err));
        } catch (err) {
          console.error(err);
          showToast('Failed to remove photo', 'error');
        } finally {
          setConfirmState(null);
        }
      },
    });
  };

  /* ---------------- Save profile ---------------- */
  const handleSaveResume = async () => {
    setSaving(true);
    try {
      const trimmedName = (formData.name || '').trim() || displayName;
      const nameChanged = trimmedName !== (userData?.name || userData?.displayName || '');
      const updates = {
        name: trimmedName,
        displayName: trimmedName,
        phone: formData.phone,
        company: formData.company,
        position: formData.position,
        education: formData.education,
        certifications: formData.certifications,
        skills: formData.skills,
        experience: formData.experience,
        bio: formData.bio,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'users', targetUid), updates);
      setUserData((prev) => ({ ...prev, ...updates }));
      setModalOpen(false);
      showToast('Profile updated successfully!');

      if (nameChanged) {
        syncAuthorFieldsOnPosts(targetUid, {
          authorName: trimmedName,
          authorPhoto: userData?.photoUrl ?? null,
        })
          .then((n) => {
            if (n > 0) console.info(`Synced name to ${n} post(s).`);
          })
          .catch((err) => console.warn('Post author sync failed:', err));
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to save', 'error');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  /* ---------------- Share profile ---------------- */
  const handleShareProfile = async () => {
    const url = `${window.location.origin}/profile/${targetUid}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: displayName,
          text: `Check out ${displayName}'s profile on IIEE-ERCSA`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        showToast('Profile link copied!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Share failed', err);
    }
  };

  /* ---------------- Delete own account ---------------- */
  const handleDeleteAccount = async () => {
    if (!isOwnProfile) return;
    if (deleting) return;

    setDeleting(true);
    try {
      const oldPath =
        userData?.photoPath ||
        getStoragePathFromDownloadUrl(userData?.photoUrl);
      if (oldPath) {
        try {
          await deleteObject(ref(storage, oldPath));
        } catch (err) {
          console.warn('Profile photo delete skipped', err);
        }
      }

      try {
        await deleteDoc(doc(db, 'users', targetUid));
      } catch (err) {
        console.warn('Firestore doc delete skipped', err);
      }

      const res = await deleteUserAccount({ uid: targetUid });
      if (!res?.data?.ok) {
        throw new Error(res?.data?.message || 'Server did not confirm deletion');
      }

      try {
        await signOut(auth);
      } catch {
        /* ignore */
      }
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Delete account failed:', err);
      const code = err?.code || '';
      let msg = err?.message || 'Failed to delete account.';
      if (code === 'functions/not-found') {
        msg =
          'Server function not deployed. Run: firebase deploy --only functions:deleteUserAccount';
      } else if (code === 'functions/permission-denied') {
        msg = 'You are not allowed to delete this account.';
      } else if (code === 'functions/unauthenticated') {
        msg = 'Please sign in again.';
      }
      if (mountedRef.current) showToast(msg, 'error');
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  /* ---------------- Derived ---------------- */
  const skillsArray = useMemo(
    () =>
      userData?.skills
        ? userData.skills.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    [userData?.skills],
  );

  const completeness = useMemo(
    () => computeProfileCompleteness(userData),
    [userData],
  );

  const roleBadge = getRoleBadge(userData);

  /* ================================================================== */
  /* Loading & Not Found                                                 */
  /* ================================================================== */

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange border-t-transparent" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <i className="fas fa-user-slash text-2xl text-gray-400" aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            User not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
            This profile doesn't exist or has been removed.
          </p>
          <Link
            to="/"
            className="inline-block bg-orange hover:bg-orange-dark text-white px-6 py-2 rounded-lg transition"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  /* ================================================================== */
  /* Render                                                              */
  /* ================================================================== */

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-4 md:py-6 px-2 md:px-4">
      <div className="container mx-auto max-w-5xl">
        {toast && (
          <Toast
            message={toast.text}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <ConfirmDialog
          open={!!confirmState}
          title={confirmState?.title}
          message={confirmState?.message}
          confirmLabel={confirmState?.confirmLabel}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
        />

        <DeleteAccountModal
          open={deleteModalOpen}
          busy={deleting}
          onClose={() => !deleting && setDeleteModalOpen(false)}
          onConfirm={handleDeleteAccount}
        />

        {isUserPending && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl flex items-start gap-3"
          >
            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-800 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="fas fa-clock text-yellow-600 dark:text-yellow-300" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">
                Account Pending Approval
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Complete your profile while waiting. You'll get full access once
                approved — usually within 24–48 hours.
              </p>
            </div>
          </motion.div>
        )}

        {/* ========== Cover + Avatar ========== */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden mb-4">
          <div className="h-32 sm:h-44 bg-gradient-to-r from-orange to-orange-dark relative">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
              aria-hidden="true"
            />
          </div>

          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-3 sm:gap-4 -mt-12 sm:-mt-16">
              <div className="relative">
                <div
                  className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center text-3xl font-bold shadow-xl ring-4 ring-white dark:ring-gray-800 overflow-hidden ${
                    dragActive ? 'opacity-60' : ''
                  }`}
                  onDrop={isOwnProfile ? handleDrop : undefined}
                  onDragOver={isOwnProfile ? handleDragOver : undefined}
                  onDragLeave={isOwnProfile ? handleDragLeave : undefined}
                >
                  {userData?.photoUrl ? (
                    <img
                      src={userData.photoUrl}
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{initial}</span>
                  )}
                </div>

                {isOwnProfile && (
                  <>
                    <label
                      className="absolute bottom-1 right-1 bg-orange text-white rounded-full w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-orange-dark transition shadow-md"
                      aria-label="Change photo"
                      title="Change photo"
                    >
                      <i className="fas fa-camera text-sm" aria-hidden="true" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                      />
                    </label>

                    {userData?.photoUrl && (
                      <button
                        type="button"
                        onClick={removePhoto}
                        aria-label="Remove photo"
                        title="Remove photo"
                        className="absolute bottom-1 left-1 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs hover:bg-red-600 transition shadow-md"
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                      </button>
                    )}

                    {uploadingPhoto && (
                      <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                        <i
                          className="fas fa-spinner fa-spin text-white text-2xl"
                          aria-hidden="true"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex-1 text-center sm:text-left sm:pb-2 min-w-0">
                <h1
                  className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-white truncate"
                  title={displayName}
                >
                  {displayName}
                </h1>
                <p className="text-orange font-medium text-sm mt-0.5 truncate">
                  {userData?.position || 'Professional Member'}
                </p>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start mt-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${roleBadge.cls}`}>
                    {roleBadge.label}
                  </span>
                  {isUserPending && (
                    <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2.5 py-0.5 rounded-full text-xs">
                      <i className="fas fa-clock mr-1" aria-hidden="true" />
                      Pending
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-2 sm:pb-2">
                {isOwnProfile ? (
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg font-medium text-sm transition flex items-center gap-2"
                  >
                    <i className="fas fa-edit" aria-hidden="true" />
                    Edit Profile
                  </button>
                ) : (
                  <Link
                    to={`/chat`}
                    className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg font-medium text-sm transition flex items-center gap-2"
                  >
                    <i className="fas fa-comment" aria-hidden="true" />
                    Message
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleShareProfile}
                  aria-label="Share profile"
                  title="Share profile"
                  className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg text-sm transition"
                >
                  <i className="fas fa-share-alt" aria-hidden="true" />
                </button>
              </div>
            </div>

            {isOwnProfile && completeness < 100 && (
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Profile completeness
                  </p>
                  <p className="text-xs font-semibold text-orange">
                    {completeness}%
                  </p>
                </div>
                <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange to-orange-dark transition-all duration-500"
                    style={{ width: `${completeness}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ========== Tabs ========== */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md mb-4 overflow-hidden">
          <div className="flex border-b dark:border-gray-700">
            <TabButton
              active={activeTab === 'about'}
              onClick={() => setActiveTab('about')}
              icon="fa-id-card"
              label="About"
            />
            <TabButton
              active={activeTab === 'resume'}
              onClick={() => setActiveTab('resume')}
              icon="fa-file-alt"
              label="Resume"
            />
            {isOwnProfile && isUserApproved && (
              <TabButton
                active={activeTab === 'events'}
                onClick={() => setActiveTab('events')}
                icon="fa-calendar-check"
                label="Events"
                count={myEvents.length}
              />
            )}
          </div>

          <div className="p-4 sm:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'about' && (
                  <div className="grid sm:grid-cols-2 gap-5">
                    <InfoRow icon="fa-user" label="Name" value={displayName} />
                    <InfoRow icon="fa-envelope" label="Email" value={userData?.email || user?.email} />
                    <InfoRow icon="fa-phone" label="Phone" value={userData?.phone} />
                    <InfoRow icon="fa-building" label="Company" value={userData?.company} />
                    <InfoRow icon="fa-briefcase" label="Position" value={userData?.position} />
                    <InfoRow
                      icon="fa-calendar"
                      label="Member Since"
                      value={userData?.createdAt ? formatDate(userData.createdAt) : 'Recently'}
                    />
                    {userData?.bio && (
                      <div className="sm:col-span-2">
                        <InfoRow icon="fa-user-tie" label="Bio" value={userData.bio} />
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'resume' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <i className="fas fa-graduation-cap text-orange" aria-hidden="true" />
                        Education
                      </h3>
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {userData?.education || (
                          <span className="text-gray-400 italic">
                            No education details added.
                          </span>
                        )}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <i className="fas fa-certificate text-orange" aria-hidden="true" />
                        Certifications &amp; Licenses
                      </h3>
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {userData?.certifications || (
                          <span className="text-gray-400 italic">
                            No certifications listed.
                          </span>
                        )}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <i className="fas fa-code-branch text-orange" aria-hidden="true" />
                        Technical Skills
                      </h3>
                      {skillsArray.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {skillsArray.map((skill, idx) => (
                            <span
                              key={idx}
                              className="bg-orange/10 text-orange px-3 py-1 rounded-full text-xs font-medium"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No skills added.</p>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <i className="fas fa-briefcase text-orange" aria-hidden="true" />
                        Professional Experience
                      </h3>
                      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                        {userData?.experience || (
                          <span className="text-gray-400 italic">
                            No experience added yet.
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'events' && isOwnProfile && isUserApproved && (
                  <div>
                    {myEvents.length === 0 ? (
                      <div className="text-center py-10">
                        <i
                          className="fas fa-calendar-times text-4xl text-gray-300 mb-3"
                          aria-hidden="true"
                        />
                        <p className="text-sm text-gray-500 mb-4">
                          You haven't registered for any events yet.
                        </p>
                        <Link
                          to="/events"
                          className="inline-block bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                        >
                          Browse Events
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {myEvents.map((event) => (
                          <div
                            key={event.id}
                            className="border-l-4 border-orange pl-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-r-lg"
                          >
                            <h3 className="font-bold text-gray-800 dark:text-white text-sm">
                              {event.title}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              <i className="far fa-calendar-alt mr-1" aria-hidden="true" />
                              {event.date ? event.date.toLocaleDateString() : 'Date TBA'}
                            </p>
                            <p className="text-xs text-gray-500">
                              <i className="fas fa-map-marker-alt mr-1" aria-hidden="true" />
                              {event.location || 'Online'}
                            </p>
                            <Link
                              to={`/events/${event.id}`}
                              className="text-orange text-xs hover:underline mt-1 inline-block font-medium"
                            >
                              View Details →
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {isUserPending && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-5 text-center border border-blue-200 dark:border-blue-800">
            <i className="fas fa-lock text-blue-500 text-2xl mb-2" aria-hidden="true" />
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
              Limited Access Mode
            </h3>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
              While pending, you can only access your profile. Full access to chat,
              feed, and members unlocks after approval.
            </p>
          </div>
        )}

        {isOwnProfile && (
          <section
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md mt-4 overflow-hidden border border-red-200 dark:border-red-900/40"
            aria-labelledby="danger-zone-title"
          >
            <div className="bg-red-50 dark:bg-red-900/20 px-5 py-3 border-b border-red-200 dark:border-red-900/40 flex items-center gap-2">
              <i className="fas fa-triangle-exclamation text-red-500" aria-hidden="true" />
              <h2
                id="danger-zone-title"
                className="font-semibold text-red-700 dark:text-red-300 text-sm"
              >
                Danger Zone
              </h2>
            </div>
            <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                  Delete Account
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Permanently delete your account and remove all associated data.
                  This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDeleteModalOpen(true)}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 self-start sm:self-auto whitespace-nowrap"
              >
                <i className="fas fa-user-slash" aria-hidden="true" />
                Delete Account
              </button>
            </div>
          </section>
        )}
      </div>

      {/* ================================================================== */}
      {/* Edit Profile Modal                                                  */}
      {/* ================================================================== */}
      <AnimatePresence>
        {modalOpen && isOwnProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setModalOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                  Edit Professional Resume
                </h2>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  aria-label="Close"
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <i className="fas fa-times text-xl" aria-hidden="true" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveResume();
                }}
                className="p-6 space-y-4"
              >
                {isUserPending && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-xs text-yellow-700 dark:text-yellow-300 flex items-start gap-2">
                    <i className="fas fa-info-circle mt-0.5" aria-hidden="true" />
                    Completing your profile helps admins review your application faster.
                  </div>
                )}

                <div>
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="edit-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone Number
                    </label>
                    <input
                      id="edit-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-position" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Job Title / Position
                    </label>
                    <input
                      id="edit-position"
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="edit-company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company / Organization
                  </label>
                  <input
                    id="edit-company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                  />
                </div>

                <div>
                  <label htmlFor="edit-education" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Education
                  </label>
                  <textarea
                    id="edit-education"
                    rows="3"
                    value={formData.education}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    placeholder="Degree, Institution, Year..."
                  />
                </div>

                <div>
                  <label htmlFor="edit-certs" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Certifications &amp; Licenses
                  </label>
                  <textarea
                    id="edit-certs"
                    rows="3"
                    value={formData.certifications}
                    onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    placeholder="PRC License, PMP, etc."
                  />
                </div>

                <div>
                  <label htmlFor="edit-skills" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Technical Skills (comma separated)
                  </label>
                  <textarea
                    id="edit-skills"
                    rows="2"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    placeholder="Power Systems, AutoCAD, Project Management"
                  />
                  {formData.skills.trim() && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {formData.skills
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map((s, i) => (
                          <span
                            key={i}
                            className="bg-orange/10 text-orange px-2 py-0.5 rounded-full text-xs"
                          >
                            {s}
                          </span>
                        ))}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="edit-experience" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Professional Experience
                  </label>
                  <textarea
                    id="edit-experience"
                    rows="4"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    placeholder="Previous roles, key projects, achievements..."
                  />
                </div>

                <div>
                  <label htmlFor="edit-bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bio / Professional Highlights
                  </label>
                  <textarea
                    id="edit-bio"
                    rows="3"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-orange"
                    placeholder="Your engineering philosophy, leadership roles..."
                  />
                </div>

                <div className="flex gap-3 pt-2 border-t dark:border-gray-700">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-orange text-white py-2 rounded-lg hover:bg-orange-dark disabled:opacity-50 font-medium transition"
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2" aria-hidden="true" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2" aria-hidden="true" />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;